import type { TileDisplay, TileSize } from "@kundeninfo/api-client";

/**
 * GRÖSSENREGEL IN DER OBERFLÄCHE. Das Web darf die Domäne nicht importieren: `applyTileChoice` und `offeredSizes` spiegeln dieselben
 * Funktionen aus `packages/domain/src/cockpitRules.ts` (K21), die Beispieltabelle `CHOICE_CASES` steht wörtlich in beiden Testdateien.
 * Die Felder sind `readonly` wie dort, damit eine `TileDefinition` und `as const`-Tabellen gleichermaßen passen.
 */

export type TileChoice = { size: TileSize; display: TileDisplay };
export type TileChoiceDefinition = { displays: readonly TileDisplay[]; sizes: readonly TileSize[] };

const isChart = (display: TileDisplay): boolean => display === "SERIES" || display === "STACKED";
/** Erste erlaubte Größe ab `2x1`: dorthin springt ein Diagramm aus `1x1`. */
const chartSize = (d: TileChoiceDefinition): TileSize | undefined => d.sizes.find((s) => s !== "1x1");

/** `1x1` nur, wenn eine Darstellung ohne Zeichenfläche erlaubt ist; sonst wäre der Knopf tot. */
export const offeredSizes = (d: TileChoiceDefinition): TileSize[] =>
  d.sizes.filter((s) => s !== "1x1" || d.displays.some((x) => !isChart(x)));

/**
 * Größenregel in beide Richtungen (650): Ein Diagramm bei `1x1` springt auf die erste erlaubte Größe ab `2x1`, `1x1` bei einem
 * Diagramm springt auf `COUNT`. Lässt sich die Regel mit den erlaubten Werten nicht halten, bleibt die Wahl ohne Wirkung, ebenso ein
 * Wert außerhalb von `displays`/`sizes`.
 */
export function applyTileChoice(current: TileChoice, change: Partial<TileChoice>, d: TileChoiceDefinition): TileChoice {
  const next = { ...current, ...change };
  if (next.size === "1x1" && isChart(next.display)) {
    if (change.display === undefined) next.display = "COUNT";
    else next.size = chartSize(d) ?? next.size;
  }
  const allowed = d.sizes.includes(next.size) && d.displays.includes(next.display)
    && !(next.size === "1x1" && isChart(next.display));
  return allowed ? next : current;
}
